import { useFeatureGate } from "@statsig/react-bindings";
import { useState } from "react";
import { useHistory, useLocation } from "react-router-dom";

import { Box } from "@/Components/v2/atoms/Box";
import { PrimaryButton } from "@/Components/v2/atoms/Button";
import { Divider } from "@/Components/v2/atoms/Divider";
import { BetaPill } from "@/Components/v2/atoms/Pill/BetaPill";
import { Stack } from "@/Components/v2/atoms/Stack";
import { TabContext, TabListV2, TabV2 } from "@/Components/v2/atoms/Tabs";
import { Typography } from "@/Components/v2/atoms/Typography";
import { featureFlag } from "@/context/FeatureFlagProvider/featureFlags";

import { useScenariosV3Routes } from "../hooks/useScenariosV3Routes";
import NewScenarioModalPage from "../pages/new-scenario-modal/NewScenarioModalPage";
import { QuickSimulateButton } from "../pages/scenarios/components/QuickSimulateButton";
import { NewActionPlanModal } from "./NewActionPlanModal/NewActionPlanModal";

const ScenarioBuilderHeader = ({ refetchScenarios }: { refetchScenarios?: () => void }) => {
  const history = useHistory();
  const location = useLocation();

  const { value: isActionPlansEnabled } = useFeatureGate(featureFlag.scenarioBuilderActionPlans);
  const { navigateToScenariosList } = useScenariosV3Routes();

  const [customBuildOpen, setCustomBuildOpen] = useState(false);
  const [newActionPlanOpen, setNewActionPlanOpen] = useState(false);

  const handleCreateNewScenario = () => {
    setCustomBuildOpen(true);
  };

  const handleCloseCustomBuild = () => {
    setCustomBuildOpen(false);
    navigateToScenariosList();
  };

  const isActive = (value: string) => {
    return location.pathname.includes(value);
  };

  const tabs = [
    {
      label: (
        <span style={{ display: "inline-flex", alignItems: "center" }}>
          Action Plans
          <BetaPill />
        </span>
      ),
      value: "action-plans",
      active: isActive("action-plans"),
    },
    {
      label: "Scenarios",
      value: "all",
      active: isActive("all"),
    },
  ];

  const activeTab = tabs.find((tab) => tab.active);

  return (
    <Stack direction="column" spacing={2}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
        <Stack spacing={1} flex={1}>
          <Typography variant="h3" fontWeight={600} fontSize="2rem">
            Scenario Builder
          </Typography>
        </Stack>
        <Stack direction="row" spacing={2}>
          <QuickSimulateButton onSimulationComplete={refetchScenarios} />
          <PrimaryButton onClick={handleCreateNewScenario} size="large">
            Custom Scenario
          </PrimaryButton>
          {/* TODO: Implement actual functionality -> dummy button for now for Demo */}
          {isActionPlansEnabled && (
            <PrimaryButton onClick={() => setNewActionPlanOpen(true)}>
              New Action Plan
            </PrimaryButton>
          )}
          <NewScenarioModalPage open={customBuildOpen} handleClose={handleCloseCustomBuild} />
          <NewActionPlanModal
            open={newActionPlanOpen}
            handleClose={() => setNewActionPlanOpen(false)}
          />
        </Stack>
      </Stack>
      {isActionPlansEnabled && (
        <Box>
          <TabContext value={activeTab?.value ?? ""}>
            <Stack direction="row" spacing={2}>
              <TabListV2 aria-label="scenario navigation tabs">
                {tabs.map((tab) => (
                  <TabV2
                    key={tab.value}
                    label={tab.label}
                    value={tab.value}
                    onClick={() => history.push(tab.value)}
                  />
                ))}
              </TabListV2>
            </Stack>
          </TabContext>
          <Divider sx={{ mt: -0.2 }} />
        </Box>
      )}
    </Stack>
  );
};

export default ScenarioBuilderHeader;
